import React, { forwardRef } from 'react';
import classNames from 'classnames';
import ErrorMessage from './ErrorMessage';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	label?: string;
	error?: string;
	required?: boolean;
	tooltip?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	({ label, error, required, tooltip, className, id, rows = 4, ...props }, ref) => {
		const inputId = id || label?.replace(/\s+/g, '-').toLowerCase();

		return (
			<div className="flex flex-col gap-1 w-full custom-input">
				{label && (
					<div className="flex items-center gap-1">
						<label htmlFor={inputId} className="text-sm text-gray-800">
							{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
						</label>
						{tooltip && (
							<div className="relative group">
								<span className="material-symbols-outlined text-gray-400 text-sm leading-none cursor-help">
									help
								</span>
								<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none">
									{tooltip}
								</div>
							</div>
						)}
					</div>
				)}
				<div className="flex relative items-center custom-input">
					<textarea
						id={inputId}
						ref={ref}
						rows={rows}
						className={classNames(
							'px-3 py-3 border rounded-md outline-none transition-all w-full resize-none',
							'placeholder:text-gray-400 text-sm !leading-relaxed',
							// eslint-disable-next-line no-nested-ternary
							props.disabled
								? 'border-gray-300 bg-gray-100 cursor-not-allowed text-gray-500 placeholder:text-gray-300'
								: error
									? 'border-red-400 bg-red-50 text-red-900 focus:border-red-400 focus:ring-1 focus:ring-[#ef4444]'
									: 'border-gray-300 hover:border-gray-400 focus:border-[var(--colour-brand-primary)] focus:ring-1 focus:ring-[var(--colour-brand-primary)]',
							props.readOnly && 'bg-gray-50 text-gray-600',
							className,
						)}
						{...props}
					/>
				</div>
				<ErrorMessage error={error} />
			</div>
		);
	},
);
